
const MidstreamDownstream = () => {
  const midstreamProjects = [
    {
      title: "Pipeline Transportation",
      description: "Chemical solutions for safe and efficient pipeline operations",
      chemicals: [
        "Drag reducing agents for flow optimization",
        "Corrosion inhibitors for pipeline protection",
        "Biocides for microbial control",
        "Hydrate inhibitors for gas pipelines"
      ]
    },
    {
      title: "Storage & Terminals",
      description: "Comprehensive chemical programs for storage facilities",
      chemicals: [
        "Tank bottom treatment chemicals",
        "Vapor space corrosion inhibitors",
        "Static dissipator additives",
        "Fuel stability improvers"
      ]
    },
    {
      title: "Gas Processing",
      description: "Specialized chemicals for gas treatment and processing",
      chemicals: [
        "Amine treating solutions",
        "Dehydration chemicals",
        "Sulfur recovery catalysts",
        "Gas sweetening additives"
      ]
    }
  ];

  const downstreamProjects = [
    {
      title: "Refinery Operations",
      description: "Advanced chemical solutions for refining processes",
      chemicals: [
        "Crude oil desalting chemicals",
        "Catalytic cracking additives",
        "Hydroprocessing chemicals",
        "Refinery water treatment"
      ]
    },
    {
      title: "Petrochemical Production",
      description: "Specialized chemicals for petrochemical manufacturing",
      chemicals: [
        "Polymerization catalysts",
        "Process optimization additives",
        "Product quality enhancers",
        "Equipment protection chemicals"
      ]
    },
    {
      title: "Product Treatment",
      description: "Chemical additives for finished product enhancement",
      chemicals: [
        "Fuel additives and stabilizers",
        "Lubricant performance improvers",
        "Antioxidants and preservatives",
        "Specialty product formulations"
      ]
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Midstream & Downstream Projects
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive chemical supply solutions for transportation, storage, refining, 
            and petrochemical operations across the value chain.
          </p>
        </div>

        {/* Midstream Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Midstream Operations</h3>
            <p className="text-lg text-gray-600">
              Chemical solutions for transportation, storage, and gas processing facilities
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {midstreamProjects.map((project, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">{project.title}</h4>
                <p className="text-gray-600 mb-4">{project.description}</p>
                <div className="space-y-2">
                  {project.chemicals.map((chemical, chemIndex) => (
                    <div key={chemIndex} className="flex items-start space-x-2">
                      <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-drop-line text-white text-xs"></i>
                      </div>
                      <p className="text-sm text-gray-700">{chemical}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Downstream Section */}
        <div>
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Downstream Operations</h3>
            <p className="text-lg text-gray-600">
              Advanced chemical solutions for refining and petrochemical processes
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {downstreamProjects.map((project, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">{project.title}</h4>
                <p className="text-gray-600 mb-4">{project.description}</p>
                <div className="space-y-2">
                  {project.chemicals.map((chemical, chemIndex) => (
                    <div key={chemIndex} className="flex items-start space-x-2">
                      <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-flask-line text-white text-xs"></i>
                      </div>
                      <p className="text-sm text-gray-700">{chemical}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Value Chain Integration */}
        <div className="mt-16 bg-gray-900 rounded-lg p-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Complete Value Chain Integration
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Golden Black provides seamless chemical supply solutions across the entire 
            oil and gas value chain, ensuring consistency, quality, and operational excellence.
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <i className="ri-oil-line text-teal-400 text-3xl mb-2"></i>
              <h4 className="text-white font-bold">Upstream</h4>
              <p className="text-gray-300 text-sm">Exploration & Production</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <i className="ri-truck-line text-blue-400 text-3xl mb-2"></i>
              <h4 className="text-white font-bold">Midstream</h4>
              <p className="text-gray-300 text-sm">Transportation & Storage</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <i className="ri-building-line text-green-400 text-3xl mb-2"></i>
              <h4 className="text-white font-bold">Downstream</h4>
              <p className="text-gray-300 text-sm">Refining & Processing</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <i className="ri-customer-service-line text-yellow-400 text-3xl mb-2"></i>
              <h4 className="text-white font-bold">Support</h4>
              <p className="text-gray-300 text-sm">Technical Services</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MidstreamDownstream;
